import {
  createEntityAdapter,
  SerializedError,
  EntityState,
} from '@reduxjs/toolkit';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';
import type { FlowTreeNode } from '@vk-hr-tek/ui/FlowTree';

import { EditorNewsListItem } from '@app/gen/news';
import {
  CollectionOption,
  CompanyEmployee,
  CompanyEventsStat,
  CompanyEventTypeItem,
  CompanyGroupUsers,
  CompanyProfiles,
  GetEventTypeWorkflowResponse,
  GetEventTypeResponse,
  GetNodeOptionsResponse,
  Group,
  CheckCompanyGroupCreationResponseItem,
  CompanyGroup,
  GroupItem,
  EditableCompany,
  CertificateError,
  RoleModelDomain,
  GetCompanyCeoResponse,
  FilterSelectOption,
  CompanyTimezone,
  GetUnitsTreeResponse,
  CompanyProfilesSettings,
  UnitTypeItem,
  TimezoneEmployee,
  TimezoneException,
  GetCompanyResponse,
} from '@app/gen/settings';
import {
  WidgetLink,
  GetAccountWidgetBannerResponse,
  Widget,
  WidgetPartition,
  WidgetPartitionCreateOption,
  GetAccountWidgetAbsencesResponse,
  NewsData,
  WidgetCompany,
  WidgetAccount,
  PostData,
  GetAccountWidgetBirthdaysResponse,
} from '@app/gen/dashboard';
import {
  Collection as CatalogCollection,
  EventType as CatalogEventType,
} from '@app/gen/event-type';
import { EmployeeItem as EmployeeToBeExcluded } from '@app/gen/events';
import { UserListItem } from '@app/gen/users';
import {
  TemplateHistory,
  Template,
  InfoTemplateResponse,
  TemplatePlaceholder,
} from '@app/gen/templates';
import { AttorneyCompany, AttorneyUser, Attorney } from '@app/gen/attorneys';
import {
  VacationAdminCompany,
  AdminVacationSchedulesListItem,
  VacationSchedule,
  VacationCustomTypeItem,
  CompanyVacationSchedule,
  SearchVacationScheduleItem,
} from '@app/gen/vacations';
import { RequestStatus, Entity } from '@app/types';
import { RootState } from '@app/store';
import {
  Account,
  AccountGroupWithCount,
  CompanyAccount,
  EditableAccount,
} from '@app/gen/account';

import { CreateVacationDto } from '../dto';
import {
  AccountDashboardException,
  AccountUserGroups,
  Company,
  EventStat,
  ExtendedSidebarMenuItem,
  GroupUser,
  ManagerSubstitutesEmployee,
  QuickActionTypeWithId,
  TemplateInformation,
  ManagerSubstitutesEventTypeItem,
} from '../types';

// Значения по умолчанию для размеров сетки
const DEFAULT_PARTITIONS_COLS = 2;
const DEFAULT_PARTITIONS_ROWS = 5;

export interface SettingsState {
  status: RequestStatus;
  companies: Company[];
  companiesByAccount: EditableCompany[];
  error: AppError | null;
  users: {
    status: RequestStatus;
    items: UserListItem[];
    error: AppError | null;
  };
  eventsStat: Record<
    string,
    {
      status: RequestStatus;
      stat: EventStat[];
      error: AppError | null;
    }
  >;
  groupUsers: Record<
    string,
    {
      status: RequestStatus;
      groups: GroupUser[];
      error: AppError | null;
    }
  >;
  managerSubstitutes: {
    status: RequestStatus;
    items: ManagerSubstitutesEmployee[];
    total: number;
    error: AppError | null;
  };
  eventTypes: Record<
    string,
    {
      status: RequestStatus;
      types: ManagerSubstitutesEventTypeItem[];
      error: AppError | null;
    }
  >;
  companyEventTypes: {
    status: RequestStatus;
    items: CompanyEventTypeItem[];
    error: AppError | null;
  };
  companyEmployees: {
    status: RequestStatus;
    items: CompanyEmployee[];
    error: AppError | null;
  };
  companyQuickActions: {
    status: RequestStatus;
    activeActions: QuickActionTypeWithId[];
    availableActions: QuickActionTypeWithId[];
    isException: boolean;
    hasParent: boolean;
    error: AppError | null;
  };
  companyEmployeeSubstitutes: {
    status: RequestStatus;
    items: CompanyEmployee[];
    error: AppError | null;
  };
  profiles: {
    status: RequestStatus;
    items: CompanyProfiles[];
    error: AppError | null;
  };
  companyGroups: {
    status: RequestStatus;
    items: Group[];
    error: AppError | null;
  };
  companySidebar: {
    status: RequestStatus;
    items: ExtendedSidebarMenuItem[];
    error: AppError | null;
    needRefetch?: boolean;
  };
  companyRoleModel: {
    status: RequestStatus;
    roleModelEnabled: boolean;
    domains: RoleModelDomain[];
    groups: Group[];
    error: AppError | null;
  };
  widgets: {
    status: RequestStatus;
    isExceptionWidget: boolean;
    canResetLayout: boolean;
    items: Widget[];
    error: AppError | null;
  };
  companyGroupsChecked: {
    status: RequestStatus;
    items: CheckCompanyGroupCreationResponseItem[];
    error: AppError | null;
  };
  checkCompanyListAccount: {
    status: RequestStatus;
    items: CheckCompanyGroupCreationResponseItem[];
    error: AppError | null;
  };
  eventType: {
    status: RequestStatus;
    item:
      | (GetEventTypeWorkflowResponse & {
          companyName: string;
          treeData: FlowTreeNode;
        })
      | null;
    error: AppError | null;
    optionsError: AppError | null;
    options: GetNodeOptionsResponse | null;
    participantsOptions: GroupItem[] | null;
    participantsOptionsError: AppError | null;
    isParticipantsExpanded: boolean;
  };
  eventTypeEdit: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    item: GetEventTypeResponse | null;
    error: AppError | null;
  };
  eventTypeCollections: {
    status: RequestStatus;
    error: AppError | null;
    items: CollectionOption[] | null;
  };
  eventTypeCompanyOptions: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    customTypeOptions: string[];
    eventTypeGroupOptions: GroupItem[];
    eventTypeCompanyEventTypes: CompanyEventTypeItem[];
  };
  templates: {
    status: RequestStatus;
    items: Template[];
    error: AppError | null;
  };
  templateHistory: {
    status: RequestStatus;
    history: TemplateHistory[];
    error: AppError | null;
  };
  templatePreview: {
    status: RequestStatus;
    data: InfoTemplateResponse | null;
    error: AppError | null;
  };
  templateUpload: {
    status: RequestStatus;
    error: AppError | null;
    templateInformation: TemplateInformation | null;
    uploadResponsePlaceholders: TemplatePlaceholder[] | null;
    newFileId: string | null;
    uploadAlertMessage: string | null;
  };
  attorneys: {
    status: RequestStatus;
    error: AppError | null;
    attorneys: Attorney[];
    total: number;
    filters: Filter[] | null;
    selected: string[];
  };
  attorneysUsers: {
    status: RequestStatus;
    error: AppError | null;
    items: AttorneyUser[];
    total: number;
    filters: Record<string, Filter[]>;
  };
  attorneysCompanies: {
    status: RequestStatus;
    error: AppError | null;
    items: AttorneyCompany[];
  };
  attorneyDetail: {
    currentId: string | null;
    status: RequestStatus;
    data: Attorney | null;
    error: AppError | null;
  };
  vacationsList: {
    status: RequestStatus;
    error: AppError | null;
    companies: AdminVacationSchedulesListItem[];
    scheduleNames: Record<string, SearchVacationScheduleItem[]>;
    schedulesStatus: 'idle' | 'loading' | 'complete' | 'failed';
    schedulesError: AppError | null;
  };
  vacationStart: {
    employeesToBeExcluded: EmployeeToBeExcluded[];
  };
  vacationCreate: {
    step: 'main' | 'filling' | 'coordination';
    status: RequestStatus;
    error: AppError | null;
    formValues: CreateVacationDto | null;
    companies: VacationAdminCompany[];
    vacationCustomTypes: {
      status: RequestStatus;
      items: VacationCustomTypeItem[];
      availableTypes: CompanyVacationSchedule['type'][];
    };
    companyGroupList: CompanyGroup[];
    unitsTree: {
      legal: Partial<GetUnitsTreeResponse>;
      operational: Partial<GetUnitsTreeResponse>;
    };
  };
  vacationEdit: {
    positions: {
      status: RequestStatus;
      hasPositions: boolean;
    };
    employees: {
      status: RequestStatus;
      hasEmployees: boolean;
    };
  };
  vacationDetail: {
    status: RequestStatus;
    data: VacationSchedule | null;
    error: AppError | null;
  };
  smevErrors: {
    status: RequestStatus;
    data: CertificateError[] | null;
    ignoreSmevForEsia: boolean;
    error: AppError | null;
  };
  companyCeo: {
    status: RequestStatus;
    data: GetCompanyCeoResponse;
    error: AppError | null;
  };
  timezoneOptions: {
    status: RequestStatus;
    items: FilterSelectOption[];
    error: AppError | null;
  };
  timezoneEmployees: {
    status: RequestStatus;
    items: TimezoneEmployee[];
    totalCount: number;
    error: AppError | null;
    filters: Filter[] | null;
  };
  timezoneExceptions: {
    status: RequestStatus;
    items: TimezoneException[];
    totalCount: number;
    error: AppError | null;
    filters: Filter[] | null;
  };
  companyTimezones: {
    status: RequestStatus;
    items: CompanyTimezone[];
    error: AppError | null;
  };
  accessLevels: {
    status: RequestStatus;
    data: string[];
    error: AppError | null;
  };
  companyAccessProfiles: {
    status: RequestStatus;
    error: AppError | null;
    data: CompanyProfilesSettings | null;
  };
  widgetContent: {
    status: RequestStatus;
    error: AppError | null;
    data: Widget[];
    total: number;
    companies: WidgetCompany[];
    accounts: WidgetAccount[];
    filters: Filter[] | null;
    filterScopeType: 'company' | 'account' | undefined;
  };
  topNewsDetail: {
    list: {
      status: RequestStatus;
      items: EditorNewsListItem[];
      images: { id: string; image: string | null }[];
      total: number;
      totalPublished: number;
      filters: Filter[] | null;
    };
    settings: {
      status: RequestStatus;
      error: AppError | null;
      items: NewsData[];
      total: number;
      type: 'type_1_4' | 'type_4';
      isException: boolean;
      widgetSize?: number;
      hasParent: boolean;
    };
  };
  feedPostsDetail: {
    settings: {
      status: RequestStatus;
      error: AppError | null;
      pinnedPostId: string | '';
      pinnedNewsId: string | '';
      isExceptionWidget: boolean;
      hasParent: boolean;
    };
    widget: Widget | null;
    filters: Filter[] | null;
    postsList: {
      status: RequestStatus;
      items: PostData[];
      total: number;
    };
    newsList: {
      status: RequestStatus;
      items: NewsData[];
      total: number;
    };
  };
  companyAccessProfilesSetup: {
    status: RequestStatus;
    error: AppError | null;
    data: CompanyProfilesSettings | null;
    unitsTrees: Record<string, Partial<GetUnitsTreeResponse>>;
    unitsTree: {
      data: Partial<GetUnitsTreeResponse>;
      status: RequestStatus;
    };
    unitTypes: {
      types: UnitTypeItem[];
      status: RequestStatus;
    };
  };
  accounts: EntityState<Account, string> & {
    status: RequestStatus;
    error: AppError | null;
  };
  accountSettings: {
    status: RequestStatus;
    accountCompanies: {
      status: RequestStatus;
      data: CompanyAccount[];
      error: AppError | null;
    };
    error: AppError | null;
  };
  accountsWithSettings: {
    status: RequestStatus;
    accounts: EditableAccount[];
    error: AppError | null;
  };
  accountRoleModel: {
    status: RequestStatus;
    roleModelEnabled: boolean;
    domains: RoleModelDomain[];
    groupsStatus: RequestStatus;
    error: AppError | null;
  };
  accountRoleModelExceptions: EntityState<CompanyAccount, string> & {
    total: number;
    currentIds: string[];
    status: RequestStatus;
    error: AppError | null;
  };
  addAccountRoleModelExceptions: {
    status: RequestStatus;
    error: AppError | null;
  };
  accountDashboardExceptions: EntityState<AccountDashboardException, string> & {
    total: number;
    currentIds: string[];
    status: RequestStatus;
    error: AppError | null;
  };
  accountBusinessTripsExceptions: EntityState<CompanyAccount, string> & {
    total: number;
    currentIds: string[];
    status: RequestStatus;
    error: AppError | null;
  };
  addAccountBusinessTripsExceptions: {
    status: RequestStatus;
    error: AppError | null;
  };
  accountGroups: {
    status: RequestStatus;
    groups: Group[];
    error: AppError | null;
  };
  accountGroupsWithCount: {
    status: RequestStatus;
    groups: AccountGroupWithCount[];
    error: AppError | null;
  };
  accountUsersGroups: EntityState<AccountUserGroups, string> & {
    total: number;
    currentIds: string[];
    status: RequestStatus;
    error: AppError | null;
  };
  accountUsersGroupCreation: {
    status: RequestStatus;
    error: AppError | null;
  };
  widgetLinks: {
    status: RequestStatus;
    items: WidgetLink[];
    error: AppError | null;
    isException: boolean;
    hasParent: boolean;
  };

  banner: {
    status: RequestStatus;
    data: GetAccountWidgetBannerResponse | null;
    error: AppError | null;
    isException: boolean;
  };
  partitions: {
    status: RequestStatus;
    isException: boolean;
    error: AppError | null;
    items: WidgetPartition[];
    cols: number;
    rows: number;
    widget: Widget | null;
    availablePartitions: WidgetPartitionCreateOption[];
    requiredPartitions: WidgetPartitionCreateOption['type'][];
    hasParent: boolean;
  };
  accountCompany: {
    status: RequestStatus;
    data: GetCompanyResponse | null;
    isSingleCompany: boolean;
  };
  absences: {
    status: RequestStatus;
    data: GetAccountWidgetAbsencesResponse | null;
    error: AppError | null;
    isException: boolean;
  };
  collections: {
    status: RequestStatus;
    items: CatalogCollection[];
    error: AppError | null;
  };
  eventTypesList: {
    status: RequestStatus;
    items: CatalogEventType[];
    collectionName: string;
    error: AppError | null;
  };
  birthdays: {
    status: RequestStatus;
    data: GetAccountWidgetBirthdaysResponse | null;
    error: AppError | null;
    isException: boolean;
  };
}

export interface SettingsWithRootState extends RootState {
  settings: EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
    SettingsState;
}

export const accountsAdapter = createEntityAdapter<Account>();
export const accountRoleModelExceptionsAdapter =
  createEntityAdapter<CompanyAccount>();
export const accountDashboardExceptionsAdapter =
  createEntityAdapter<AccountDashboardException>();
export const accountBusinessTripsExceptionsAdapter =
  createEntityAdapter<CompanyAccount>();
export const accountUsersGroupsAdapter =
  createEntityAdapter<AccountUserGroups>();

export const initialState: SettingsState = {
  status: 'idle',
  companies: [],
  companiesByAccount: [],
  error: null,
  users: {
    status: 'idle',
    items: [],
    error: null,
  },
  eventsStat: {},
  groupUsers: {},
  managerSubstitutes: {
    status: 'idle',
    items: [],
    total: 0,
    error: null,
  },
  eventTypes: {},
  companyEventTypes: {
    status: 'idle',
    items: [],
    error: null,
  },
  companySidebar: {
    items: [],
    status: 'idle',
    error: null,
    needRefetch: undefined,
  },
  companyQuickActions: {
    status: 'idle',
    activeActions: [],
    availableActions: [],
    isException: false,
    hasParent: false,
    error: null,
  },
  companyEmployees: {
    status: 'idle',
    items: [],
    error: null,
  },
  companyEmployeeSubstitutes: {
    status: 'idle',
    items: [],
    error: null,
  },
  profiles: {
    status: 'idle',
    items: [],
    error: null,
  },
  companyGroups: {
    status: 'idle',
    items: [],
    error: null,
  },
  companyRoleModel: {
    status: 'idle',
    roleModelEnabled: false,
    domains: [],
    groups: [],
    error: null,
  },
  companyGroupsChecked: {
    status: 'idle',
    items: [],
    error: null,
  },
  checkCompanyListAccount: {
    status: 'idle',
    items: [],
    error: null,
  },
  widgets: {
    status: 'idle',
    isExceptionWidget: false,
    canResetLayout: false,
    items: [],
    error: null,
  },
  eventType: {
    status: 'idle',
    item: null,
    error: null,
    optionsError: null,
    options: null,
    participantsOptions: null,
    participantsOptionsError: null,
    isParticipantsExpanded: false,
  },
  eventTypeEdit: {
    status: 'idle',
    item: null,
    error: null,
  },
  eventTypeCollections: {
    status: 'idle',
    items: null,
    error: null,
  },
  eventTypeCompanyOptions: {
    status: 'idle',
    customTypeOptions: [],
    eventTypeCompanyEventTypes: [],
    eventTypeGroupOptions: [],
    error: null,
  },
  templates: {
    status: 'idle',
    items: [],
    error: null,
  },
  templateHistory: {
    status: 'idle',
    history: [],
    error: null,
  },
  templatePreview: {
    status: 'idle',
    data: null,
    error: null,
  },
  templateUpload: {
    status: 'idle',
    error: null,
    templateInformation: null,
    uploadResponsePlaceholders: null,
    newFileId: null,
    uploadAlertMessage: null,
  },
  attorneys: {
    status: 'idle',
    attorneys: [],
    error: null,
    total: 0,
    filters: null,
    selected: [],
  },
  attorneysUsers: {
    status: 'idle',
    error: null,
    items: [],
    total: 0,
    filters: {},
  },
  attorneysCompanies: {
    status: 'idle',
    error: null,
    items: [],
  },
  attorneyDetail: {
    currentId: null,
    status: 'idle',
    data: null,
    error: null,
  },
  vacationsList: {
    status: 'idle',
    error: null,
    companies: [],
    scheduleNames: {},
    schedulesStatus: 'idle',
    schedulesError: null,
  },
  vacationStart: {
    employeesToBeExcluded: [],
  },
  vacationCreate: {
    step: 'main',
    error: null,
    status: 'idle',
    formValues: null,
    companies: [],
    companyGroupList: [],
    unitsTree: {
      legal: {},
      operational: {},
    },
    vacationCustomTypes: {
      status: 'idle',
      items: [],
      availableTypes: [],
    },
  },
  vacationEdit: {
    positions: {
      status: 'idle',
      hasPositions: false,
    },
    employees: {
      status: 'idle',
      hasEmployees: false,
    },
  },
  vacationDetail: {
    status: 'idle',
    data: null,
    error: null,
  },
  smevErrors: {
    status: 'idle',
    data: null,
    ignoreSmevForEsia: false,
    error: null,
  },
  companyCeo: {
    status: 'idle',
    data: {},
    error: null,
  },
  timezoneOptions: {
    status: 'idle',
    items: [],
    error: null,
  },
  timezoneExceptions: {
    status: 'idle',
    items: [],
    totalCount: 0,
    error: null,
    filters: null,
  },
  timezoneEmployees: {
    status: 'idle',
    items: [],
    totalCount: 0,
    error: null,
    filters: null,
  },
  companyTimezones: {
    status: 'idle',
    items: [],
    error: null,
  },
  accessLevels: {
    status: 'idle',
    data: [],
    error: null,
  },
  companyAccessProfiles: {
    status: 'idle',
    error: null,
    data: null,
  },
  widgetContent: {
    status: 'idle',
    error: null,
    data: [],
    total: 0,
    companies: [],
    accounts: [],
    filters: null,
    filterScopeType: undefined,
  },
  topNewsDetail: {
    list: {
      status: 'idle',
      items: [],
      images: [],
      total: 0,
      totalPublished: 0,
      filters: null,
    },
    settings: {
      status: 'idle',
      error: null,
      items: [],
      total: 0,
      type: 'type_1_4',
      isException: false,
      widgetSize: undefined,
      hasParent: false,
    },
  },
  feedPostsDetail: {
    settings: {
      status: 'idle',
      error: null,
      pinnedPostId: '',
      pinnedNewsId: '',
      isExceptionWidget: true,
      hasParent: false,
    },
    widget: null,
    filters: null,
    postsList: {
      status: 'idle',
      items: [],
      total: 0,
    },
    newsList: {
      status: 'idle',
      items: [],
      total: 0,
    },
  },
  companyAccessProfilesSetup: {
    status: 'idle',
    error: null,
    data: null,
    unitsTrees: {},
    unitsTree: {
      data: {},
      status: 'idle',
    },
    unitTypes: {
      types: [],
      status: 'idle',
    },
  },
  accounts: accountsAdapter.getInitialState({ status: 'idle', error: null }),
  accountCompany: {
    status: 'idle',
    data: null,
    isSingleCompany: true,
  },
  accountSettings: {
    status: 'idle',
    accountCompanies: {
      status: 'idle',
      data: [],
      error: null,
    },
    error: null,
  },
  accountsWithSettings: {
    status: 'idle',
    accounts: [],
    error: null,
  },
  accountRoleModel: {
    status: 'idle',
    roleModelEnabled: false,
    domains: [],
    groupsStatus: 'idle',
    error: null,
  },
  accountRoleModelExceptions: accountRoleModelExceptionsAdapter.getInitialState(
    { total: 0, currentIds: [], status: 'idle', error: null },
  ),
  addAccountRoleModelExceptions: {
    status: 'idle',
    error: null,
  },
  accountDashboardExceptions: accountDashboardExceptionsAdapter.getInitialState(
    {
      total: 0,
      currentIds: [],
      status: 'idle',
      error: null,
    },
  ),
  accountBusinessTripsExceptions:
    accountBusinessTripsExceptionsAdapter.getInitialState({
      total: 0,
      currentIds: [],
      status: 'idle',
      error: null,
    }),
  addAccountBusinessTripsExceptions: {
    status: 'idle',
    error: null,
  },
  accountUsersGroups: accountUsersGroupsAdapter.getInitialState({
    total: 0,
    currentIds: [],
    status: 'idle',
    error: null,
  }),
  accountGroups: {
    status: 'idle',
    groups: [],
    error: null,
  },
  accountGroupsWithCount: {
    status: 'idle',
    groups: [],
    error: null,
  },
  accountUsersGroupCreation: {
    status: 'idle',
    error: null,
  },
  widgetLinks: {
    status: 'idle',
    items: [],
    error: null,
    isException: false,
    hasParent: false,
  },
  banner: {
    status: 'idle',
    data: null,
    error: null,
    isException: false,
  },
  partitions: {
    status: 'idle',
    isException: false,
    error: null,
    items: [],
    cols: DEFAULT_PARTITIONS_COLS,
    rows: DEFAULT_PARTITIONS_ROWS,
    widget: null,
    availablePartitions: [],
    requiredPartitions: [],
    hasParent: false,
  },
  absences: {
    status: 'idle',
    data: null,
    error: null,
    isException: false,
  },
  birthdays: {
    status: 'idle',
    data: null,
    error: null,
    isException: false,
  },
  collections: {
    status: 'idle',
    items: [],
    error: null,
  },
  eventTypesList: {
    status: 'idle',
    items: [],
    collectionName: '',
    error: null,
  },
};

export const startLoading = (state: SettingsState) => {
  state.status = 'loading';
  state.error = null;
  state.companies = [];
  state.companiesByAccount = [];
};

export const completeLoading = (state: SettingsState) => {
  state.status = 'complete';
  state.error = null;
};

export const setError = (
  state: SettingsState,
  { payload, error }: { payload?: AppError; error: SerializedError },
) => {
  state.status = 'failed';
  state.error =
    payload ||
    ({
      info: (error && error.message) || 'Unknown error',
      status: 500,
      source: 'client',
      title: 'Internal client error',
    } as AppError);
};

export const resetUploadState = (state: SettingsState) => {
  state.templateUpload.uploadAlertMessage = null;
  state.templateUpload.newFileId = null;
  state.templateUpload.uploadResponsePlaceholders = null;
};

export const settingsAdapter =
  createEntityAdapter<Entity<CompanyEventsStat | CompanyGroupUsers>>();
